import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { isUserLoggedIn, clearAuthData } from '../utils/authUtils';
import { useVerificationStatus } from '../context/VerificationStatusContext';
import { auth0Config } from '../config/auth0Config';
import VerificationPending from './VerificationPending';
import VerificationRejected from './VerificationRejected';
import { getAgentSellerVerificationStatus } from '../api/marketplace';

interface ProtectedRouteProps {
  element: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const { isLoading } = useAuth0();
  const location = useLocation();
  const { status, setStatus, isLoading: statusLoading, setIsLoading, setRejectionReason } = useVerificationStatus();

  // Check if user has valid credentials
  const hasAuth = isUserLoggedIn();

  // Fetch verification status on first load
  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {        

        const response = await getAgentSellerVerificationStatus()
        
        // Extract status from response
        if (response?.data && response.data.status) {
          setStatus(response.data.status);
          
          // If rejected, store the rejection reason
          if (response.data.status === 'Rejected' && response.data.reason) {
            setRejectionReason(response.data.reason);
          }
        }
        setIsLoading(false);
      } catch (error: any) {
        console.error('Failed to fetch verification status:', error);
        
        // Handle 403 Forbidden error
        if (error.response?.status === 403) {
          // Clear auth data from localStorage
          clearAuthData();
          
          // Redirect to Auth0 login page
          const logoutUrl = `https://${auth0Config.domain}/v2/logout?client_id=${auth0Config.clientId}&returnTo=${window.location.origin}`;
          window.location.href = logoutUrl;
          
          return;
        }
        
        setIsLoading(false);
      }
    };

    if (hasAuth && status === null) {
      fetchVerificationStatus();
    }
  }, [hasAuth, status, setStatus, setIsLoading]);

  // Show loading state while Auth0 is initializing or status is being fetched
  if (isLoading || (hasAuth && status === null)) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  // If no authentication, redirect to login page
  if (!hasAuth) {
    return <Navigate to="/login" replace />;
  }

  // Check verification status and handle routing
  if (status === 'NotStarted') {
    // User hasn't started verification - only allow Profile page
    if (location.pathname === '/profile') {
      return element;
    }
    // Redirect all other pages to profile
    return <Navigate to="/profile" replace />;
  }

  if (status === 'Pending') {
    // User verification is pending - show pending screen
    // Don't allow access to other protected pages
    return <VerificationPending />;
  }

  if (status === 'Rejected') {
    // User verification is rejected - show rejection screen
    // Don't allow access to other protected pages
    return <VerificationRejected />;
  }

  // if (status === 'Approved') {
  //   // User is approved - allow access to profile in view-only mode
  //   if (location.pathname === '/profile') {
  //     return element;
  //   }
  //   // Redirect all other pages to profile (read-only)
  //   return <Navigate to="/profile" replace />;
  // }

  // Status is 'Verified' or other - allow access to requested page
  return element;
};

export default ProtectedRoute;
